import { ActualFmpReportedTagExtension } from './extentions/actualFmpReportedTag';
import { EffectiveOpenPageExtension } from './extentions/effectiveOpenPage';
import { getGlobalProps } from './internal/inappGlobalProps';
import { publish, subscribe } from './internal/metricSubscription';
import { sendCustomRequestEvent, sendFirstScreenRequestEvent, sendActualFMPEvent, sendRenderStartEvent, sendRenderEndEvent, } from './internal/onDemandMetric';
import { Deferred, sleep } from './internal/util';
let _globalState = null;
const globalTTAPPluginstateMap = new Map();
function getTTAPPluginState(slardarClient) {
    const state = slardarClient ? globalTTAPPluginstateMap.get(slardarClient) : _globalState;
    if (!state) {
        throw new Error('Please register the plugin first.');
    }
    return state;
}
function setTTAPPluginState(state) {
    /**
     *  Compatible with historical logic.
     */
    if (!_globalState) {
        _globalState = { ...state };
    }
    globalTTAPPluginstateMap.set(state.slardarClient, state);
}
/**
 * Register TTAP Plugin
 */
export const registerTTAPPlugin = async (config) => {
    try {
        if (!(typeof window !== 'undefined' && config.slardar && !globalTTAPPluginstateMap.has(config.slardar))) {
            return Promise.resolve();
        }
        const { slardar, scenario } = config;
        const state = {
            register$: new Deferred(),
            slardarClient: slardar,
            scenarioInstance: scenario(slardar),
        };
        void registerClient(config, state.slardarClient, state.scenarioInstance, state.register$);
        setTTAPPluginState(state);
        return state.register$.promise;
    }
    catch {
        return Promise.resolve();
    }
};
async function preRegisterTrigger(scenarioInstance, extensions, slardarClient) {
    await scenarioInstance.preRegister();
    await Promise.all((extensions !== null && extensions !== void 0 ? extensions : []).map((ext) => ext.preRegister({
        slardar: slardarClient,
    })));
}
function postRegisterTrigger(scenarioInstance, extensions, slardarClient) {
    scenarioInstance.postRegister();
    for (const extension of extensions !== null && extensions !== void 0 ? extensions : []) {
        void extension.postRegister({
            slardar: slardarClient,
            subscribeMetric: subscribe,
            scenarioState: scenarioInstance.getState(),
        });
    }
}
async function registerClient(config, slardarClient, scenarioInstance, register$) {
    var _a, _b;
    scenarioInstance.setRegisterStart();
    const internalExtensions = [new ActualFmpReportedTagExtension(), new EffectiveOpenPageExtension()];
    const preRegister$ = preRegisterTrigger(scenarioInstance, [...internalExtensions, ...((_a = config.extensions) !== null && _a !== void 0 ? _a : [])], slardarClient);
    await Promise.race([preRegister$, sleep(300)]);
    await slardarClient.registerClient({
        disableEnvAutoConfig: config.disableEnvAutoConfig,
        disableReleaseVersionAutoConfig: config.disableReleaseVersionAutoConfig,
        env: config.env,
        release: config.release,
        isSpa: (_b = config.isSpa) !== null && _b !== void 0 ? _b : false,
        ignoreApiUrl: config.ignoreApiUrl,
        includeApiUrl: config.includeApiUrl,
        vmok: config.vmok,
    });
    scenarioInstance.setRegisterEnd();
    register$.resolve();
    // This sleep is used to make the main application to resume first before postProcess functions,
    // because the main application doesn't need any information from the postProcess executions.
    await sleep(1);
    postRegisterTrigger(scenarioInstance, config.extensions, slardarClient);
}
function reportFirstScreenRequestInternal(firstScreenRequests, state) {
    var _a, _b;
    if (!state.slardarClient || state.isFirstScreenRequestSent) {
        return;
    }
    if ((_b = (_a = state.slardarClient) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.isSpaRouteCheck) {
        return;
    }
    if (firstScreenRequests.length === 0) {
        return;
    }
    sendFirstScreenRequestEvent(firstScreenRequests, state.slardarClient);
    state.isFirstScreenRequestSent = true;
}
export function reportFirstScreenRequest(startTimestamp, endTimestamp, slardarClient) {
    const state = getTTAPPluginState(slardarClient);
    reportFirstScreenRequestInternal([{ startTime: startTimestamp, endTime: endTimestamp }], state);
}
export const reportActualFMP = (slardarClient) => {
    var _a, _b;
    try {
        const state = getTTAPPluginState(slardarClient);
        if (!state.slardarClient || !state.scenarioInstance) {
            return 0;
        }
        if ((_b = (_a = state.slardarClient) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.isSpaRouteCheck) {
            return 0;
        }
        if (!state.isFirstScreenRequestSent) {
            const firstScreenRequests = state.scenarioInstance.getFirstScreenRequests();
            reportFirstScreenRequestInternal(firstScreenRequests, state);
        }
        if (!state.actualFMPDuration) {
            state.actualFMPDuration = sendActualFMPEvent(state.slardarClient);
            publish('actualFmp', state.actualFMPDuration);
        }
        return state.actualFMPDuration;
    }
    catch {
        return 0;
    }
};
/**
 * Report ActualLCP metric duration.
 * The duration is calculated from navigationStart.
 * @deprecated please use reportActualFMP
 * @returns {number} ActualLCP duration
 */
export const reportActualLCP = () => {
    return reportActualFMP();
};
/**
 * globalProps.containerInitTime - navigationStart
 * @returns {number} container init total time
 */
export const getContainerInitTotalTime = () => {
    return getGlobalProps('containerInitTotalTime');
};
export function reportCustomRequest(requestName, startTime, endTime, slardarClient) {
    var _a, _b;
    try {
        const state = getTTAPPluginState(slardarClient);
        if (!state.slardarClient) {
            return;
        }
        if ((_b = (_a = state.slardarClient) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.isSpaRouteCheck) {
            return;
        }
        sendCustomRequestEvent(requestName, startTime, endTime, state.slardarClient);
    }
    catch {
        return;
    }
}
/**
 * Helper to trigger Slardar.start only after registerTTAPPlugin is completed
 */
export const startMonitor = async (slardarClient) => {
    const state = getTTAPPluginState(slardarClient);
    if (!state.slardarClient || !state.register$) {
        return;
    }
    await state.register$.promise;
    state.slardarClient.start();
};
/**
 *
 * Only work for unit-test
 */
export const setStateForTesting = (override) => {
    _globalState = { ...(_globalState !== null && _globalState !== void 0 ? _globalState : {}), ...override };
    setTTAPPluginState(_globalState);
};
export const cleanStateForTesting = () => {
    _globalState = null;
    globalTTAPPluginstateMap.clear();
};
export const reportRenderStart = (slardarClient) => {
    var _a, _b;
    try {
        const state = getTTAPPluginState(slardarClient);
        if (!state.slardarClient || !state.scenarioInstance) {
            return 0;
        }
        // eslint-disable-next-line @ttfe/@typescript-eslint/no-unnecessary-condition
        if ((_b = (_a = state.slardarClient) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.isSpaRouteCheck) {
            return 0;
        }
        if (!state.renderStartDuration) {
            state.renderStartDuration = sendRenderStartEvent(state.slardarClient);
        }
        return state.renderStartDuration;
    }
    catch {
        return 0;
    }
};
export const reportRenderEnd = (slardarClient) => {
    var _a, _b;
    try {
        const state = getTTAPPluginState(slardarClient);
        if (!state.slardarClient || !state.scenarioInstance) {
            return 0;
        }
        if ((_b = (_a = state.slardarClient) === null || _a === void 0 ? void 0 : _a.getState()) === null || _b === void 0 ? void 0 : _b.isSpaRouteCheck) {
            return 0;
        }
        if (!state.renderEndDuration) {
            state.renderEndDuration = sendRenderEndEvent(state.slardarClient);
        }
        return state.renderEndDuration;
    }
    catch {
        return 0;
    }
};
//# sourceMappingURL=main.js.map